import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Hammer, Plus, Edit2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ITEM_TYPES = ['pavers', 'bricks', 'briquettes', 'blocks', 'tiles', 'other'];

const empty = { item_type: 'pavers', name: '', quantity: '', unit: 'pcs', unit_price: '', notes: '' };

const MasonryInventorySection = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'project_manager', 'finance_officer']);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...empty });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/masonry/inventory`);
      setItems(res.data);
    } catch { toast.error('Failed to load masonry inventory'); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        quantity: parseFloat(form.quantity || 0),
        unit_price: parseFloat(form.unit_price || 0),
      };
      if (editing) {
        await axios.put(`${API_URL}/api/masonry/inventory/${editing.id}`, payload);
        toast.success('Updated');
      } else {
        await axios.post(`${API_URL}/api/masonry/inventory`, payload);
        toast.success('Item added');
      }
      setOpen(false); setEditing(null); setForm({ ...empty });
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this stock item?')) return;
    await axios.delete(`${API_URL}/api/masonry/inventory/${id}`);
    fetchItems();
  };

  const totalValue = items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading flex items-center gap-2"><Hammer className="h-4 w-4" /> Masonry Stock Inventory</CardTitle>
          <CardDescription>Pavers, bricks, briquettes & other production output · Total value: {formatCurrency(totalValue)}</CardDescription>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => { setEditing(null); setForm({ ...empty }); setOpen(true); }} data-testid="add-masonry-stock-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Stock
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Quantity</TableHead>
              <TableHead className="text-right">Unit Price</TableHead>
              <TableHead className="text-right">Value</TableHead>
              {canEdit && <TableHead className="w-24"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No stock recorded yet</TableCell></TableRow>
            ) : items.map((i) => (
              <TableRow key={i.id} data-testid={`masonry-row-${i.id}`}>
                <TableCell><Badge variant="outline" className="capitalize">{i.item_type}</Badge></TableCell>
                <TableCell>{i.name || '—'}</TableCell>
                <TableCell>{i.quantity} {i.unit || 'pcs'}</TableCell>
                <TableCell className="text-right">{i.unit_price ? formatCurrency(i.unit_price) : '—'}</TableCell>
                <TableCell className="text-right font-medium">{formatCurrency((i.quantity || 0) * (i.unit_price || 0))}</TableCell>
                {canEdit && (
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(i); setForm({ ...empty, ...i }); setOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(i.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit' : 'Add'} Masonry Stock</DialogTitle>
            <DialogDescription>Record stock from masonry training & production</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Item Type *</Label>
                <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                  <SelectTrigger data-testid="masonry-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ITEM_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Name / Variant</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Red Pavers" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Quantity *</Label><Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required /></div>
              <div className="space-y-1.5"><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="pcs, kg" /></div>
              <div className="space-y-1.5"><Label>Unit Price</Label><Input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="masonry-submit-btn">{editing ? 'Update' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MasonryInventorySection;
